import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { updateInstallOnboarding } from "../store/slices/installSlice";
import { postOnboardingStatus } from "../api/clientOnboarding";
import { waitForAppBootstrap } from "../helper/appBootstrap";
import { fetchWidgetsFromDb } from "../helper/widgetApi";
import { hasAnyWidgets } from "../utils/widgetEventsIndex";

/**
 * `clients.is_on_boarding` same scale as AgeShield (0–5).
 * Step 1: at least one widget exists in DB (auto-sync when API returns widgets).
 * Step 2 embed auto-sync: triggers once step 1 is satisfied and the embed is on.
 */
export function useCalendarOnboarding({ installChecked, installData, themeLoading, embedInactive }) {
  const dispatch = useDispatch();
  const onboardingFromStore = installData?.shop_info?.is_on_boarding ?? "0";
  const clientId = installData?.shop_info?.id;
  const backendStatus = parseInt(String(onboardingFromStore), 10);
  const safeBackend = Number.isFinite(backendStatus) ? backendStatus : 0;

  const setupGuideDismissed = safeBackend === 4 || safeBackend === 5;

  const installReadyRef = useRef(false);
  useEffect(() => {
    if (installChecked && installData) {
      installReadyRef.current = true;
    }
  }, [installChecked, installData]);

  const [widgetsChecked, setWidgetsChecked] = useState(false);
  const [hasWidgets, setHasWidgets] = useState(false);

  useEffect(() => {
    const cid = installData?.shop_info?.id;
    if (!installChecked || !cid) {
      setWidgetsChecked(false);
      setHasWidgets(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        await waitForAppBootstrap();
        const list = await fetchWidgetsFromDb({ force: true });
        if (cancelled) return;
        setHasWidgets(hasAnyWidgets(list));
        setWidgetsChecked(true);
      } catch (e) {
        console.warn("[useCalendarOnboarding] widgets fetch:", e);
        if (!cancelled) {
          setHasWidgets(false);
          setWidgetsChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [installChecked, installData?.shop_info?.id]);

  const applyStatus = useCallback(
    (status) => {
      dispatch(updateInstallOnboarding(String(status)));
    },
    [dispatch],
  );

  const persist = useCallback(
    async (status) => {
      if (!clientId) {
        return { success: false, message: "No client" };
      }
      try {
        const res = await postOnboardingStatus(status);
        if (res?.success) {
          applyStatus(status);
          return { success: true };
        }
        return { success: false, message: res?.message };
      } catch (e) {
        console.error("[useCalendarOnboarding] persist:", e);
        return { success: false };
      }
    },
    [clientId, applyStatus],
  );

  /**
   * Sync DB with real state: widget exists → at least 1; embed on → at least 2 only after widgets.
   * Skip when status is 3+ so we never overwrite dismissed or completed flows.
   */
  useEffect(() => {
    if (!installReadyRef.current || !clientId || themeLoading || !widgetsChecked) return;
    if (safeBackend === 3 || safeBackend === 4 || safeBackend === 5) return;

    if (hasWidgets && safeBackend < 1) {
      let cancelled = false;
      (async () => {
        const r = await persist("1");
        if (!cancelled && !r.success) {
          console.warn("[useCalendarOnboarding] Could not sync step 1 to DB");
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (!embedInactive && hasWidgets && safeBackend < 2) {
      let cancelled = false;
      (async () => {
        const r = await persist("2");
        if (!cancelled && !r.success) {
          console.warn("[useCalendarOnboarding] Could not sync step 2 to DB");
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [embedInactive, themeLoading, clientId, safeBackend, persist, widgetsChecked, hasWidgets]);

  const markWidgetStep = useCallback(async () => {
    if (safeBackend === 4 || safeBackend === 5) {
      return { success: true };
    }
    if (!hasWidgets) {
      return { success: false, message: "Create a widget first." };
    }
    if (safeBackend >= 1) {
      return { success: true };
    }
    return persist("1");
  }, [safeBackend, hasWidgets, persist]);

  const markSetupComplete = useCallback(async () => {
    if (safeBackend === 3 || safeBackend === 5) {
      return { success: true };
    }
    if (!hasWidgets) {
      return { success: false, message: "Create a widget first." };
    }
    if (embedInactive) {
      return { success: false, message: "Turn on the theme app embed first." };
    }
    if (safeBackend === 4) {
      return persist("5");
    }
    return persist("3");
  }, [safeBackend, hasWidgets, embedInactive, persist]);

  const dismissSetupGuide = useCallback(async () => {
    if (safeBackend === 4 || safeBackend === 5) {
      return { success: true };
    }
    const next = safeBackend >= 3 ? "5" : "4";
    return persist(next);
  }, [safeBackend, persist]);

  /** Step 1 = widget exists. DB status alone does not mark step 1 if the store has zero widgets. */
  const step1Done = hasWidgets;
  const step2Done = !embedInactive;
  const step3Done = safeBackend === 3 || safeBackend === 5;
  const completedCount = [step1Done, step2Done, step3Done].filter(Boolean).length;

  return {
    onboardingStatus: onboardingFromStore,
    setupGuideDismissed,
    widgetsChecked,
    step1Done,
    step2Done,
    step3Done,
    completedCount,
    markWidgetStep,
    markSetupComplete,
    dismissSetupGuide,
    persist,
  };
}
